import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {getOffers, getCouponPoints} from '../api/offer.api';
import {RootState} from './store';

interface coupon {
  couponCode: string;
  couponDesc: string;
  validTillDate: string;
}
interface CustomerCoupon {
  customerCouponId: number;
  mobileNumber: string;
  couponGeneratedTime: string;
  couponCode: string;
  description: string;
  validity: string;
  keyId: string;
  couponType: string;
  regionId: string;
  detailsId: number;
}
interface CouponPoints {
  tier: string | null;
  message: string;
  firstName: string | null;
  redirectUrl: string | null;
  pointsAvailable: number;
  pointsToExpire: number;
  firstScan: null;
  date: string | null;
  keyId: string | null;
  benefit: null;
  firstLogin: string | null;
  showEdge: boolean;
  customerCoupons: CustomerCoupon[] | null;
  spinWheelPoints: null;
  interactionBenefit: null;
  resultType: string;
}

interface offerState {
  offers: coupon[] | null;
  loading: boolean;
  error: string;
  couponPoints: CouponPoints | null;
  customerCouponsCount: number;
}

const initialState: offerState = {
  offers: null,
  loading: false,
  error: '',
  couponPoints: null,
  customerCouponsCount: 0,
};

interface OfferRequest {
  payload: {
    mobileNumber: string | null;
    ouCode: string | null;
  };
  accessToken: string | null;
}

interface CouponPointsRequest {
  accessToken: string | null;
  payload: {
    keyId: string | null;
    storecode?: string | null;
  };
}

export const fetchCouponPoints = createAsyncThunk(
  'offers/fetchCouponPoints',
  async (parameters: CouponPointsRequest, thunkApi) => {
    return getCouponPoints(parameters)
      .then(response => {
        return response;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(error.response.data);
      });
  },
);

export const fetchOffers = createAsyncThunk(
  'offers/fetchOffers',
  async (parameters: OfferRequest, thunkApi) => {
    return getOffers(parameters)
      .then(response => {
        return response.couponList;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(error.response.data);
      });
  },
);

export const offerSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    setCustomerCouponsCount: (state, action: PayloadAction<number>) => {
      state.customerCouponsCount = action.payload;
    },
    resetCouponAPIResponse: state => {
      state.couponPoints = null;
      state.error = '';
    },
    resetOfferState: state => {
      state.offers = null;
      state.error = '';
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchOffers.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchOffers.fulfilled, (state, action) => {
      state.loading = false;
      state.offers = action.payload;
      state.error = '';
    });
    builder.addCase(fetchOffers.rejected, (state, action) => {
      state.error = action.toString();
      state.loading = false;
    });
    builder.addCase(fetchCouponPoints.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchCouponPoints.fulfilled, (state, action) => {
      state.loading = false;
      state.couponPoints = action.payload;
      state.error = '';
    });
    builder.addCase(fetchCouponPoints.rejected, (state, action) => {
      state.error = action.toString();
      state.loading = false;
    });
  },
});

export const {
  setCustomerCouponsCount,
  resetCouponAPIResponse,
  resetOfferState,
} = offerSlice.actions;

export const offers = (state: RootState): coupon[] | null =>
  state.offerReducer.offers;
export const couponPoints = (state: RootState): CouponPoints | null =>
  state.offerReducer.couponPoints;
export const errorInCouponApi = (state: RootState): string =>
  state.offerReducer.error;
export const couponIsLoading = (state: RootState): boolean =>
  state.offerReducer.loading;

export default offerSlice.reducer;
